import {
    BASE_VALUE,
    BulkSelectionContextValue,
} from '@/components/bulkQueue/tables/BulkSelectionContext';
import {
    Entity,
    useBulkSelectionStore,
    useBulkState,
    useSelectedEntityIdsKey,
} from '@/components/bulkQueue/tables/bulkContextUtils';
import { BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY } from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateAssignmentTable';
import {
    BulkTentativeDateRecommendationsQuery,
    BulkTentativeDateRecommendationsQueryVariables,
} from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateContext.gql.types';
import {
    UseAssignmentDateRecsResult,
    useAssignmentDateRecs,
} from '@/components/decisionSupport/hooks/useAssignmentDateRecs';
import {
    VISIT_RECOMMENDED_DATES_FRAGMENT,
    VISIT_REQUEST_RECOMMENDED_DATES_FRAGMENT,
} from '@/components/decisionSupport/queries/dateRecommendations';
import { DateRecommendationFragment } from '@/components/decisionSupport/queries/dateRecommendations.gql.types';
import {
    canSelectVisit,
    pickTopRanked,
} from '@/components/decisionSupport/utils/decisionSupport.utils';
import { VisitOverrideState } from '@/components/decisionSupport/utils/visitAssignmentInput.utils';
import {
    BULK_UPDATE_TENTATIVE_DATE_MUTATION,
    BULK_UPDATE_VISIT_TENTATIVE_DATE_MUTATION,
} from '@/components/visitRequests/mutations/updateTentativeDate';
import {
    BulkUpdateVisitRequestTentativeDateMutation,
    BulkUpdateVisitRequestTentativeDateMutationVariables,
    BulkUpdateVisitTentativeDateMutation,
    BulkUpdateVisitTentativeDateMutationVariables,
} from '@/components/visitRequests/mutations/updateTentativeDate.gql.types';
import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import { ALL_VISIT_COUNTS } from '@/pages/AllVisitsPage/AllVisits.gql';
import { errorToast } from '@/toast';
import { FeatureFlag } from '@/types/featureFlag';
import {
    ProviderStore,
    ProviderWritableStore,
    createDefaultProviderStore,
    createDefaultWritableProviderStore,
    createStore,
    useStoreValue,
} from '@/utils/store';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { UpdateVisitRequestTentativeDateInput, UpdateVisitTentativeDateInput } from 'gql-types';
import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

export const BULK_TENTATIVE_DATE_RECOMMENDATIONS = gql`
    query BulkTentativeDateRecommendations(
        $visitIds: [UUID!]!
        $excludeVisits: Boolean!
        $visitRequestIds: [UUID!]!
        $excludeVisitRequests: Boolean!
    ) {
        decisionSupport {
            visitRequests(filters: { id: { inList: $visitRequestIds } })
                @skip(if: $excludeVisitRequests) {
                __typename
                dateRecommendations {
                    ...VisitRequestRecommendedDates
                }
            }
            visits(filters: { id: { inList: $visitIds } }) @skip(if: $excludeVisits) {
                __typename
                dateRecommendations {
                    ...VisitRecommendedDates
                }
            }
        }
    }
    ${VISIT_RECOMMENDED_DATES_FRAGMENT}
    ${VISIT_REQUEST_RECOMMENDED_DATES_FRAGMENT}
`;

type AssignmentDateResult = UseAssignmentDateRecsResult['resultsByVisitId'] extends ReadonlyMap<
    string,
    infer V
>
    ? V
    : never;
export type AssignmentDateRecommendation = AssignmentDateResult['dateRecommendation'];
type AssignmentDateDefinitions = UseAssignmentDateRecsResult['definitions'];

type BulkTentativeDateContextValue = BulkSelectionContextValue<{
    recommendationsStore: ProviderStore<DateRecommendationFragment[]>;
    selectedRecommendationStore: ProviderWritableStore<string | null>;
    // New DS visitAssignment flow (DS_VISIT_ASSIGNMENT_REWRITE). When `hasRewrite`
    // is true the cells read date recommendations from these instead of the legacy
    // `recommendationsStore`; the selected date string + submit are unchanged.
    hasRewrite: boolean;
    assignmentDefinitions: AssignmentDateDefinitions;
    assignmentResultsByVisitId: UseAssignmentDateRecsResult['resultsByVisitId'];
    assignmentSelectableIds: string[];
    // True when the sibling bulk-cancel selection column has a selection. While
    // disabled, every date checkbox + the select-all header checkbox are locked so
    // the two columns stay mutually exclusive.
    disabled: boolean;
}>;

const DEFAULT_VALUE: BulkTentativeDateContextValue = {
    ...BASE_VALUE,
    recommendationsStore: createDefaultProviderStore<DateRecommendationFragment[]>([]),
    selectedRecommendationStore: createDefaultWritableProviderStore<string | null>(null),
    hasRewrite: false,
    assignmentDefinitions: null,
    assignmentResultsByVisitId: new Map(),
    assignmentSelectableIds: [],
    disabled: false,
};

export const useUpdateTentativeDates = () => {
    return useMutation<
        BulkUpdateVisitRequestTentativeDateMutation,
        BulkUpdateVisitRequestTentativeDateMutationVariables
    >(BULK_UPDATE_TENTATIVE_DATE_MUTATION, {
        onError: (error) => {
            errorToast(error.message);
        },
        refetchQueries: [ALL_VISIT_COUNTS, BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY],
        awaitRefetchQueries: true,
    });
};

export const useUpdateVisitTentativeDates = () => {
    return useMutation<
        BulkUpdateVisitTentativeDateMutation,
        BulkUpdateVisitTentativeDateMutationVariables
    >(BULK_UPDATE_VISIT_TENTATIVE_DATE_MUTATION, {
        onError: (error) => {
            errorToast(error.message);
        },
        refetchQueries: [ALL_VISIT_COUNTS, BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY],
        awaitRefetchQueries: true,
    });
};

const BulkTentativeDateContext = createContext<BulkTentativeDateContextValue>(DEFAULT_VALUE);

type BulkTentativeDateContextProviderProps = {
    children: ReactNode;
    entities: Entity[];
    /** Lock all date selection when the sibling bulk-cancel column has a selection. */
    disabled?: boolean;
};

export const BulkTentativeDateContextProvider = (props: BulkTentativeDateContextProviderProps) => {
    const { children, entities, disabled = false } = props;
    const [isEntitySelectedStore, setValue, selectedCount, resetSelection, pruneToEntityIds] =
        useBulkSelectionStore();
    const recommendationsStoreRef = useRef(createStore<DateRecommendationFragment[]>());
    const recommendationsStore = recommendationsStoreRef.current;
    const selectedRecommendationStoreRef = useRef(createStore<string | null>());
    const selectedRecommendationStore = selectedRecommendationStoreRef.current;

    // Prune selections when the displayed entities change (e.g. filter change).
    useEffect(() => {
        const entityIds = new Set(entities.map((e) => e.id));
        pruneToEntityIds(entityIds);
    }, [entities, pruneToEntityIds]);

    const visitIds = useMemo(
        () => entities.filter((entity) => entity.__typename === 'Visit').map((entity) => entity.id),
        [entities],
    );

    const visitRequestIds = useMemo(
        () =>
            entities
                .filter((entity) => entity.__typename === 'VisitRequest')
                .map((entity) => entity.id),
        [entities],
    );

    const hasRewrite = useFeatureFlagEnabled(FeatureFlag.DS_VISIT_ASSIGNMENT_REWRITE);

    // New DS visitAssignment flow (visits only). Date recommendations hold the
    // visit's currently-assigned clinician fixed automatically (implicit keep —
    // we send no clinician override) and choose the date.
    const visitOverrideStates = useMemo<VisitOverrideState[]>(
        () =>
            hasRewrite
                ? entities
                      .filter((entity) => entity.__typename === 'Visit')
                      .map((entity) => ({
                          visitId: entity.id,
                          appointments: [],
                          dateOverride: undefined,
                      }))
                : [],
        [hasRewrite, entities],
    );
    const {
        definitions: assignmentDefinitions,
        resultsByVisitId: assignmentResultsByVisitId,
        loading: assignmentLoading,
    } = useAssignmentDateRecs(visitOverrideStates);

    // Legacy fetch handles VisitRequests (and all entities when the flag is off).
    // When the flag is on, exclude visits so they only flow through the new query.
    const legacyVisitIds = hasRewrite ? [] : visitIds;
    const excludeVisits = legacyVisitIds.length === 0;
    const excludeVisitRequests = visitRequestIds.length === 0;

    const { data, previousData, loading } = useQuery<
        BulkTentativeDateRecommendationsQuery,
        BulkTentativeDateRecommendationsQueryVariables
    >(BULK_TENTATIVE_DATE_RECOMMENDATIONS, {
        variables: {
            visitIds: legacyVisitIds,
            excludeVisits,
            visitRequestIds,
            excludeVisitRequests,
        },
        skip: excludeVisits && excludeVisitRequests,
        fetchPolicy: 'network-only',
    });

    const currentData = data ?? previousData;
    const hasInitializedRef = useRef(false);

    // Legacy population (flag off, or VisitRequest entities).
    useEffect(() => {
        if (hasRewrite) {
            return;
        }
        const recommendations = new Map<string, DateRecommendationFragment[]>();
        const selectedDates = new Map<string, string | null>();

        const dateRecommendations = [
            ...(currentData?.decisionSupport.visits?.dateRecommendations ?? []),
            ...(currentData?.decisionSupport.visitRequests?.dateRecommendations ?? []),
        ];
        for (const rec of dateRecommendations) {
            const entityId = 'visitRequest' in rec ? rec.visitRequest.id : rec.visit.id;
            const dates = rec.recommendedDates;
            recommendations.set(entityId, dates);

            // Auto-select the best recommendation
            const bestDate = dates
                .slice()
                .sort((a, b) => a.recommendationRank - b.recommendationRank)
                .at(0);
            if (bestDate) {
                selectedDates.set(entityId, GraphInputHelpers.date(bestDate.date));
                // Auto-select the entity for submission only on initial load
                if (!hasInitializedRef.current) {
                    setValue(entityId, true);
                }
            }
        }

        recommendationsStore.setAll(recommendations);
        selectedRecommendationStore.setAll(selectedDates);
        hasInitializedRef.current = true;
    }, [hasRewrite, currentData, recommendationsStore, selectedRecommendationStore, setValue]);

    // Reset picks when the recs change (a new DS set); identical recs keep the same ref.
    useEffect(() => {
        if (!hasRewrite) {
            return;
        }
        selectedRecommendationStore.setAll(new Map());
    }, [hasRewrite, assignmentResultsByVisitId, selectedRecommendationStore]);

    const getDefaultDate = useCallback(
        (entityId: string): string | null => {
            const recs =
                assignmentResultsByVisitId.get(entityId)?.dateRecommendation.recommendations;
            const best = pickTopRanked(recs);
            return best ? GraphInputHelpers.date(best.date) : null;
        },
        [assignmentResultsByVisitId],
    );

    // Recomputes the selectable set (Select All + header count) when the user picks;
    // the selection store mutates outside React (see useSelectedEntityIdsKey).
    const pickedVisitIdsKey = useSelectedEntityIdsKey(
        selectedRecommendationStore,
        (date) => date != null,
    );

    // A visit is selectable when it has a recommended default OR an explicit pick
    const assignmentSelectableIds = useMemo<string[]>(() => {
        if (!hasRewrite) {
            return [];
        }
        const pickedVisitIds = new Set(pickedVisitIdsKey ? pickedVisitIdsKey.split(',') : []);
        return entities
            .filter((entity) => entity.__typename === 'Visit')
            .filter((entity) =>
                canSelectVisit(
                    (assignmentResultsByVisitId.get(entity.id)?.dateRecommendation.recommendations
                        .length ?? 0) > 0,
                    pickedVisitIds.has(entity.id),
                ),
            )
            .map((entity) => entity.id);
    }, [hasRewrite, entities, assignmentResultsByVisitId, pickedVisitIdsKey]);

    const [mutateVisitRequests, { loading: submittingVisitRequests }] = useUpdateTentativeDates();
    const [mutateVisits, { loading: submittingVisits }] = useUpdateVisitTentativeDates();
    const submitting = submittingVisitRequests || submittingVisits;

    const entityTypeMap = useMemo(
        () => new Map(entities.map((e) => [e.id, e.__typename])),
        [entities],
    );

    const submit = useCallback(async () => {
        const selected = isEntitySelectedStore.getAll();
        const visitRequests: UpdateVisitRequestTentativeDateInput[] = [];
        const visits: UpdateVisitTentativeDateInput[] = [];

        selected.forEach((isSelected: boolean, entityId: string) => {
            if (!isSelected) {
                return;
            }
            const date =
                selectedRecommendationStore.getSnapshot(entityId) ?? getDefaultDate(entityId);
            if (!date) {
                return;
            }

            if (entityTypeMap.get(entityId) === 'Visit') {
                visits.push({ visitId: entityId, tentativeDate: date });
            } else {
                visitRequests.push({ visitRequestId: entityId, tentativeDate: date });
            }
        });

        const mutations = [];
        if (visitRequests.length > 0) {
            mutations.push(mutateVisitRequests({ variables: { input: { visitRequests } } }));
        }
        if (visits.length > 0) {
            mutations.push(mutateVisits({ variables: { input: { visits } } }));
        }
        await Promise.all(mutations);
        resetSelection();
    }, [
        mutateVisitRequests,
        mutateVisits,
        isEntitySelectedStore,
        selectedRecommendationStore,
        getDefaultDate,
        resetSelection,
        entityTypeMap,
    ]);

    const state = useBulkState(loading || assignmentLoading, submitting);

    const value: BulkTentativeDateContextValue = {
        selectedRecommendationStore: {
            ...selectedRecommendationStore,
            setValue: selectedRecommendationStore.setValue,
        },
        recommendationsStore,
        isEntitySelectedStore: {
            ...isEntitySelectedStore,
            setValue,
        },
        submit,
        state,
        selectedCount,
        entityCount: entities.length,
        hasRewrite,
        assignmentDefinitions,
        assignmentResultsByVisitId,
        assignmentSelectableIds,
        disabled,
    };

    return (
        <BulkTentativeDateContext.Provider value={value}>
            {children}
        </BulkTentativeDateContext.Provider>
    );
};

const getSelectableEntityIds = (
    recommendationsStore: ProviderStore<DateRecommendationFragment[]>,
): string[] => {
    const ids: string[] = [];
    recommendationsStore.getAll().forEach((recommendations, entityId) => {
        if (recommendations.length > 0) {
            ids.push(entityId);
        }
    });
    return ids;
};

export const useBulkTentativeDateContext = () => {
    const {
        submit,
        state,
        selectedCount,
        entityCount,
        recommendationsStore,
        isEntitySelectedStore,
        hasRewrite,
        assignmentSelectableIds,
        disabled,
    } = useContext(BulkTentativeDateContext);
    const summary = useTentativeDateSummary();

    const legacySelectableCount = useSyncExternalStore(
        (cb: () => void) => recommendationsStore.subscribeToAll(cb),
        () => getSelectableEntityIds(recommendationsStore).length,
    );
    const selectableCount = hasRewrite ? assignmentSelectableIds.length : legacySelectableCount;

    const selectAll = useCallback(() => {
        const selectableIds = hasRewrite
            ? assignmentSelectableIds
            : getSelectableEntityIds(recommendationsStore);
        const currentlySelected = isEntitySelectedStore.getAll();
        const allSelected =
            selectableIds.length > 0 &&
            selectableIds.every((id) => currentlySelected.get(id) === true);
        selectableIds.forEach((id) => {
            isEntitySelectedStore.setValue?.(id, !allSelected);
        });
    }, [hasRewrite, assignmentSelectableIds, recommendationsStore, isEntitySelectedStore]);

    return useMemo(
        () => ({
            submit,
            state,
            selectedCount,
            entityCount,
            summary,
            selectableCount,
            selectAll,
            disabled,
        }),
        [selectedCount, summary, entityCount, state, submit, selectableCount, selectAll, disabled],
    );
};

export const useTentativeDateSummary = () => {
    const { selectedRecommendationStore, isEntitySelectedStore } =
        useContext(BulkTentativeDateContext);

    return useSyncExternalStore(
        (cb: () => void) => {
            const unsub1 = selectedRecommendationStore.subscribeToAll(cb);
            const unsub2 = isEntitySelectedStore.subscribeToAll(cb);
            return () => {
                unsub1();
                unsub2();
            };
        },
        () => {
            const dates = new Set<string>();
            const selected = isEntitySelectedStore.getAll();
            selected.forEach((isSelected: boolean, entityId: string) => {
                if (!isSelected) {
                    return;
                }
                const date = selectedRecommendationStore.getSnapshot(entityId);
                if (date) {
                    dates.add(date);
                }
            });
            const numSelected = Array.from(selected.values()).filter(Boolean).length;
            const dateList = Array.from(dates).join(', ');
            return `Assign date${dates.size > 1 ? 's' : ''} to ${numSelected} visits: ${dateList}`;
        },
    );
};

export const useDateRecommendations = (
    entityId: string,
): [recommendations: DateRecommendationFragment[], isLoading: boolean] => {
    const { state } = useContext(BulkTentativeDateContext);
    const recommendations = useStoreValue(
        BulkTentativeDateContext,
        'recommendationsStore',
        entityId,
    );

    return [recommendations ?? [], state === 'loading'];
};

// Whether the new DS visitAssignment flow is active for this table.
export const useDateRewriteEnabled = (): boolean => useContext(BulkTentativeDateContext).hasRewrite;

// Per-visit date recommendation + shared definitions for the new flow.
export const useAssignmentDateRec = (
    entityId: string,
): {
    definitions: AssignmentDateDefinitions;
    dateRecommendation: AssignmentDateRecommendation | null;
} => {
    const { assignmentDefinitions, assignmentResultsByVisitId } =
        useContext(BulkTentativeDateContext);
    return {
        definitions: assignmentDefinitions,
        dateRecommendation: assignmentResultsByVisitId.get(entityId)?.dateRecommendation ?? null,
    };
};

export const useIsEntitySelected = (entityId: string): [boolean, (isSelected: boolean) => void] => {
    const { isEntitySelectedStore } = useContext(BulkTentativeDateContext);
    const isSelected = useStoreValue(BulkTentativeDateContext, 'isEntitySelectedStore', entityId);

    const setIsSelected = useCallback(
        (selected: boolean) => {
            isEntitySelectedStore.setValue?.(entityId, selected);
        },
        [isEntitySelectedStore, entityId],
    );

    return [isSelected, setIsSelected];
};

export const useSelectedDate = (
    entityId: string,
): [string | null, (date: string | null) => void] => {
    const { selectedRecommendationStore, isEntitySelectedStore } =
        useContext(BulkTentativeDateContext);
    const selectedDate = useStoreValue(
        BulkTentativeDateContext,
        'selectedRecommendationStore',
        entityId,
    );

    const setSelectedDate = useCallback(
        (date: string | null) => {
            selectedRecommendationStore.setValue(entityId, date);
            // Auto-select the entity when a date is chosen
            if (date) {
                isEntitySelectedStore.setValue?.(entityId, true);
            }
        },
        [selectedRecommendationStore, isEntitySelectedStore, entityId],
    );

    return [selectedDate ?? null, setSelectedDate];
};
